import { useState, useEffect } from "react";
import api from "../api/api"; // adjust the relative path if dashboardPurchase.jsx isn't a sibling of api.js
import {
  ShoppingCart,
  IndianRupee,
  Receipt,
  Truck,
  Target,
  ArrowUpRight,
  ArrowRight,
  BarChart3,
  ShieldAlert
} from 'lucide-react';

// ---- Indian currency helpers -------------------------------------------
// Full grouped rupee value, e.g. 642890 -> "₹6,42,890"
const formatINR = (value) =>
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value)}`;

// Compact Lakh / Crore value, e.g. 1850000 -> "₹18.5L", 125000000 -> "₹12.5Cr"
const formatCompactINR = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1e7) return `₹${(value / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(value / 1e5).toFixed(1)}L`;
  if (abs >= 1e3) return `₹${(value / 1e3).toFixed(1)}K`;
  return formatINR(value);
};

// Round a value up to a clean axis ceiling (1 / 2 / 5 / 10 × a power of ten) so chart
// gridlines and scaling stay proportionate no matter what the underlying data is.
const getNiceMax = (value) => {
  if (!value || value <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)));
};

export default function PurchaseDashboard() {
  const [activeQuarterTab, setActiveQuarterTab] = useState("All");

  // ---- Live dashboard data ------------------------------------------------
  // { totalPurchaseOrders, totalPOValue, totalDistributorInvoiceValue,
  //   totalOtherExpenses, invoicePending, invoiceReceived, quarterlyPOValue,
  //   monthlyPOValue, recentPurchases, pendingInvoices, fyLabel }
  // NOTE: this dashboard is intentionally NOT scoped to the current user —
  // it always shows purchase data across everyone.
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadMetrics() {
      try {
        setMetricsLoading(true);
        setMetricsError(null);
        // Uses the shared axios instance from ./api so the token is attached
        // and expired-token refresh is handled the same way as your other
        // endpoints (leads, deals, purchases, etc).
        const res = await api.get("/dashboard/purchase-metrics");
        if (!cancelled) setMetrics(res.data);
      } catch (err) {
        if (!cancelled) {
          setMetricsError(
            err.response?.data?.error || err.message || "Failed to load purchase dashboard data"
          );
        }
      } finally {
        if (!cancelled) setMetricsLoading(false);
      }
    }

    loadMetrics();
    return () => {
      cancelled = true;
    };
  }, []);

  // Quarter-filtered PO value for the top card + chart. "All" shows the
  // lifetime total; picking a specific quarter narrows to that FY quarter.
  const selectedQuarterPO =
    activeQuarterTab !== "All"
      ? (metrics?.quarterlyPOValue || []).find((q) => q.quarter === activeQuarterTab)
      : null;
  const displayedPOValue = selectedQuarterPO ? selectedQuarterPO.value : metrics?.totalPOValue ?? 0;

  const invoicePending = metrics?.invoicePending || { count: 0, value: 0 };
  const invoiceReceived = metrics?.invoiceReceived || { count: 0, value: 0 };
  const invoiceTotalCount = invoicePending.count + invoiceReceived.count;

  // Core Metrics (Items i to iv) — built from live `metrics` once loaded
  const topMetrics = [
    {
      label: "Total Purchase Orders",
      value: metricsLoading ? "—" : (metrics?.totalPurchaseOrders ?? 0).toLocaleString("en-IN"),
      icon: ShoppingCart,
      color: "blue",
      description: "Distributor POs raised",
      subtext:
        "All purchases across the team" +
        (activeQuarterTab === "All" ? "" : ` · ${activeQuarterTab}`)
    },
    {
      label: "Total Purchase Value",
      value: metricsLoading ? "—" : formatINR(displayedPOValue),
      icon: IndianRupee,
      color: "green",
      description: "Gross PO value (f1_po_value)",
      subtext: metricsLoading
        ? ""
        : activeQuarterTab === "All"
        ? (metrics?.fyLabel ? `As of ${metrics.fyLabel}` : "")
        : `${activeQuarterTab} · ${metrics?.fyLabel || ""}`
    },
    {
      label: "Distributor Invoices",
      value: metricsLoading ? "—" : formatINR(metrics?.totalDistributorInvoiceValue ?? 0),
      icon: Receipt,
      color: "amber",
      description: "Invoiced value from distributors",
      subtext: metricsLoading
        ? ""
        : `${invoiceReceived.count} received · ${invoicePending.count} pending`
    },
    {
      label: "Transport & Other Costs",
      value: metricsLoading ? "—" : formatINR(metrics?.totalOtherExpenses ?? 0),
      icon: Truck,
      color: "blue",
      description: "Transport expenses + other credits",
      subtext: "Logged against distributor POs"
    }
  ];

  // Quarterly PO Value Trend line chart — live PO-value totals per quarter.
  const allTrendData = metrics?.quarterlyPOValue ?? ["Q1", "Q2", "Q3", "Q4"].map((q) => ({ quarter: q, value: 0 }));
  const trendData =
    activeQuarterTab === "All" ? allTrendData : allTrendData.filter((d) => d.quarter === activeQuarterTab);

  // Fixed internal coordinate space for the SVG (independent of rendered size) so the
  // chart geometry is computed once from the data rather than hand-placed per point.
  const trendSvgWidth = 640;
  const trendSvgHeight = 176;
  const trendPadX = 40;
  const trendTopMargin = 12;
  const trendMaxRaw = Math.max(...allTrendData.map((d) => d.value), 0);
  const trendMax = getNiceMax(trendMaxRaw);
  const trendGridlines = [1, 0.75, 0.5, 0.25].map((f) => trendMax * f);
  const trendPlotWidth = trendSvgWidth - trendPadX * 2;
  const trendXStep = trendData.length > 1 ? trendPlotWidth / (trendData.length - 1) : 0;
  const trendYForValue = (value) =>
    trendSvgHeight - trendTopMargin - (value / trendMax) * (trendSvgHeight - trendTopMargin);
  const trendPoints = trendData.map((d, i) => ({
    x: trendData.length === 1 ? trendPadX + trendPlotWidth / 2 : trendPadX + i * trendXStep,
    y: trendYForValue(d.value)
  }));
  const trendLinePath =
    trendPoints.length > 1 ? trendPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ") : "";
  const trendAreaPath =
    trendPoints.length > 1
      ? `M ${trendPoints[0].x} ${trendSvgHeight} ` +
        trendPoints.map((p) => `L ${p.x} ${p.y}`).join(" ") +
        ` L ${trendPoints[trendPoints.length - 1].x} ${trendSvgHeight} Z`
      : "";
  const peakQuarter = trendData.reduce((max, d) => (d.value > max.value ? d : max), trendData[0] || { quarter: "-", value: 0 });

  // Item vi (repurposed): Invoice Status Panel — received vs pending value,
  // shown as progress bars against total PO value (mirrors the "Target
  // Assignments" panel's shape but driven entirely by live invoice data).
  const invoiceStatusRows = [
    {
      label: "Distributor Invoices Received",
      value: invoiceReceived.value,
      count: invoiceReceived.count,
      total: metrics?.totalPOValue || 0,
      color: "bg-emerald-500"
    },
    {
      label: "Distributor Invoices Pending",
      value: invoicePending.value,
      count: invoicePending.count,
      total: metrics?.totalPOValue || 0,
      color: "bg-amber-500"
    }
  ].map((row) => ({
    ...row,
    progress: row.total > 0 ? Math.round((row.value / row.total) * 100) : 0
  }));

  // Item vii (repurposed): Monthly Purchase Value bar chart — FY-ordered
  // (Apr->Mar), narrowed to the selected quarter's 3 months when applicable.
  const FY_MONTH_FALLBACK = [
    ["Apr", "Q2"], ["May", "Q2"], ["Jun", "Q2"],
    ["Jul", "Q3"], ["Aug", "Q3"], ["Sep", "Q3"],
    ["Oct", "Q4"], ["Nov", "Q4"], ["Dec", "Q4"],
    ["Jan", "Q1"], ["Feb", "Q1"], ["Mar", "Q1"],
  ].map(([month, quarter]) => ({ month, quarter, value: 0 }));
  const allMonthlyData = metrics?.monthlyPOValue?.length ? metrics.monthlyPOValue : FY_MONTH_FALLBACK;
  const monthlyData =
    activeQuarterTab === "All" ? allMonthlyData : allMonthlyData.filter((d) => d.quarter === activeQuarterTab);
  const rawMaxMonthly = Math.max(...allMonthlyData.map((d) => d.value), 1);
  const maxMonthly = getNiceMax(rawMaxMonthly);
  const monthlyGridlines = [1, 0.75, 0.5, 0.25].map((f) => maxMonthly * f);
  const barHeightPct = (value) => Math.max((value / maxMonthly) * 100, value > 0 ? 3 : 0);
  const avgMonthlyValue =
    monthlyData.length > 0 ? monthlyData.reduce((s, d) => s + d.value, 0) / monthlyData.length : 0;
  const fyTotalPOValue = allMonthlyData.reduce((s, d) => s + d.value, 0);

  const recentPurchases = metrics?.recentPurchases || [];
  const pendingInvoices = metrics?.pendingInvoices || [];

  return (
    <div className="min-h-screen bg-[#F4F7F9] font-sans p-6 mt-16 ml-16">
      <div className="max-w-8xl space-y-6">

        {/* DASHBOARD HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 pb-1">
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
              Purchase Dashboard
              <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                Org-wide
              </span>
            </h1>
            <p className="text-[12px] text-slate-400 mt-0.5">Distributor purchase orders &amp; invoicing · FY {metrics?.fyLabel?.replace("FY", "") || "26–27"}</p>
            {metricsError && (
              <p className="text-[11px] text-red-500 mt-1">Couldn't load live data: {metricsError}</p>
            )}
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg p-1 text-[11px] font-semibold">
            {["All", "Q1", "Q2", "Q3", "Q4"].map((q) => (
              <button
                key={q}
                onClick={() => setActiveQuarterTab(q)}
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  activeQuarterTab === q ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-50"
                }`}
              >
                {q}
              </button>
            ))}
          </div>
        </div>

        {/* UPPER SUMMARY METRICS BLOCK */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
          {topMetrics.map((card) => (
            <div
              key={card.label}
              className="group relative bg-white border border-slate-200 rounded-xl p-4 transition-all duration-300 hover:shadow-lg hover:border-blue-500/30 cursor-pointer overflow-hidden"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-2.5 rounded-lg transition-all duration-300 ${
                    card.color === 'blue' ? 'bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white' :
                    card.color === 'amber' ? 'bg-amber-50 text-amber-600 group-hover:bg-amber-600 group-hover:text-white' :
                    'bg-green-50 text-green-600 group-hover:bg-green-600 group-hover:text-white'
                  }`}>
                    <card.icon size={18} strokeWidth={2.5} />
                  </div>

                  <div>
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      {card.label}
                    </h3>
                    <div className="flex items-baseline space-x-1">
                      <span className="text-2xl font-black text-slate-900 tracking-tight">
                        {card.value}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 pt-2 border-t border-slate-50 flex flex-col gap-0.5">
                <p className="text-[11px] font-medium text-slate-600">{card.description}</p>
                <p className="text-[10px] text-slate-400">{card.subtext}</p>
              </div>

              {/* Bottom Theme Accent Line */}
              <div className={`absolute bottom-0 left-0 h-0.5 transition-all duration-300 group-hover:w-full w-0 ${
                card.color === 'blue' ? 'bg-blue-500' :
                card.color === 'amber' ? 'bg-amber-500' : 'bg-green-500'
              }`} />
            </div>
          ))}
        </div>

        {/* MIDDLE SECTION: PO VALUE TREND CHART & INVOICE STATUS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* QUARTERLY PURCHASE VALUE TREND LINE CHART */}
          <div className="bg-white border border-slate-200 rounded-xl lg:col-span-2 flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b bg-white">
              <div>
                <span className="text-sm font-semibold text-slate-800 block">Financial Year Purchase Process</span>
                <span className="text-[11px] text-gray-400">
                  {activeQuarterTab === "All"
                    ? "Quarterly PO value raised against distributors"
                    : `Showing ${activeQuarterTab} PO value`}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-slate-500">
                <div className="flex items-center gap-1"><span className="w-2.5 h-0.5 bg-blue-600 inline-block" /> PO Value</div>
              </div>
            </div>

            {/* SVG Vector Line Chart Container */}
            <div className="p-6 flex-1 flex flex-col justify-between bg-white min-h-[280px]">
              <div className="relative w-full h-44 mt-2">

                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                  {trendGridlines.map((g) => (
                    <div key={g} className="w-full border-t border-slate-100 text-[9px] text-slate-400 pt-0.5">
                      {formatCompactINR(g)}
                    </div>
                  ))}
                  <div className="w-full border-b border-slate-200 text-[9px] text-slate-400 pb-0.5">0</div>
                </div>

                <svg
                  className="absolute inset-0 w-full h-full"
                  viewBox={`0 0 ${trendSvgWidth} ${trendSvgHeight}`}
                  preserveAspectRatio="none"
                >
                  <defs>
                    <linearGradient id="purchaseChartGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" stopOpacity="0.15" />
                      <stop offset="100%" stopColor="#2563eb" stopOpacity="0.00" />
                    </linearGradient>
                  </defs>

                  <path d={trendAreaPath} fill="url(#purchaseChartGradient)" />
                  <path d={trendLinePath} fill="none" stroke="#2563eb" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

                  {trendPoints.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r="5" fill="#ffffff" stroke="#2563eb" strokeWidth="3" className="cursor-pointer" />
                  ))}
                </svg>

                <div className="absolute inset-x-0 bottom-0 flex justify-between px-[15px] translate-y-6 text-xs font-bold text-slate-600">
                  {trendData.map((d) => (
                    <div key={d.quarter} className="text-center w-12">
                      {d.quarter}
                      <span className="block text-[9px] text-slate-400 font-mono font-medium">{formatCompactINR(d.value)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 bg-slate-50 rounded-lg p-3 flex items-center justify-between text-xs text-slate-500">
                <span>
                  {activeQuarterTab === "All" ? "Peak Quarter" : `${activeQuarterTab} Purchases`}:{" "}
                  <strong>{peakQuarter.quarter} ({formatINR(peakQuarter.value)})</strong>
                </span>
                <span className="text-blue-600 font-medium flex items-center gap-0.5">
                  Invoice recovery: {metrics?.totalPOValue ? Math.round((invoiceReceived.value / metrics.totalPOValue) * 100) : 0}% <ArrowUpRight size={14} />
                </span>
              </div>
            </div>
          </div>

          {/* DISTRIBUTOR INVOICE STATUS PANEL */}
          <div className="bg-white border border-slate-200 rounded-xl flex flex-col justify-between overflow-hidden">
            <div className="p-4 border-b bg-white flex items-center gap-2">
              <Target size={18} className="text-blue-600" />
              <div>
                <span className="text-sm font-semibold text-slate-800 block">Invoice Status</span>
                <span className="text-[11px] text-gray-400">Distributor invoice recovery vs PO value</span>
              </div>
            </div>

            <div className="p-4 divide-y divide-slate-100 flex-1 flex flex-col justify-between space-y-3">
              {invoiceStatusRows.map((row) => (
                <div key={row.label} className="pt-2.5 first:pt-0 group">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-700">{row.label}</span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                      {formatINR(row.value)} <span className="text-slate-300">({row.count})</span>
                    </span>
                  </div>

                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-1.5">
                    <div
                      className={`h-full ${row.color} transition-all duration-500 rounded-full`}
                      style={{ width: `${Math.min(row.progress, 100)}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between mt-1 text-[10px]">
                    <span className="text-gray-400 font-medium">of total PO value</span>
                    <span className="font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                      {row.progress}%
                    </span>
                  </div>
                </div>
              ))}
              {invoiceTotalCount === 0 && !metricsLoading && (
                <p className="text-[11px] text-slate-400 text-center pt-2">No purchase records yet.</p>
              )}
            </div>

            <div className="bg-slate-50 p-3 text-center border-t border-slate-100 text-[11px] text-gray-500">
              Reflects distributor_invoice_no capture on each PO.
            </div>
          </div>

        </div>

        {/* LOWER GRID: RECENT PURCHASES & PENDING INVOICES */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* RECENT PURCHASES TABLE */}
          <div className="bg-white border border-slate-200 rounded-xl lg:col-span-2 overflow-hidden">
            <div className="p-4 border-b bg-white flex justify-between items-center">
              <div>
                <span className="text-sm font-semibold text-slate-800 block">Recent Purchases</span>
                <span className="text-[11px] text-gray-400">Latest distributor POs and their invoice status</span>
              </div>
              <button className="text-xs text-blue-600 font-semibold hover:underline flex items-center gap-0.5">
                Export <ArrowRight size={14} />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-400 tracking-wider border-b border-slate-100">
                  <tr>
                    <th className="p-3 pl-4">PO Number</th>
                    <th className="p-3">Distributor</th>
                    <th className="p-3">Deal Ref</th>
                    <th className="p-3 text-right">PO Value</th>
                    <th className="p-3 text-center pr-4">Invoice</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {recentPurchases.map((purchase) => (
                    <tr key={purchase.purchaseId} className="hover:bg-slate-50/70 transition cursor-pointer text-[13px]">
                      <td className="p-3 pl-4 font-mono font-bold text-blue-600">{purchase.poNumber || `#${purchase.purchaseId}`}</td>
                      <td className="p-3 font-medium text-slate-800">
                        {purchase.distributorName || "—"}
                        {purchase.clientName && (
                          <span className="block text-[10px] text-slate-400 font-normal">{purchase.clientName}</span>
                        )}
                      </td>
                      <td className="p-3 text-slate-500 text-xs font-mono">OPF-{purchase.opfId}</td>
                      <td className="p-3 text-right font-semibold text-slate-900 font-mono">{formatINR(purchase.poValue)}</td>
                      <td className="p-3 text-center pr-4">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          purchase.invoiceStatus === "Received" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" :
                          "bg-amber-50 text-amber-700 border border-amber-100"
                        }`}>
                          {purchase.invoiceStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {recentPurchases.length === 0 && !metricsLoading && (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-xs text-slate-400">No purchase records found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* PENDING DISTRIBUTOR INVOICES */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col justify-between">
            <div className="p-4 border-b bg-white flex items-center gap-2">
              <ShieldAlert size={17} className="text-rose-600" />
              <div>
                <span className="text-sm font-semibold text-slate-800 block">Invoices Awaited</span>
                <span className="text-[11px] text-gray-400">POs still missing a distributor invoice</span>
              </div>
            </div>

            <div className="divide-y divide-slate-100 flex-1">
              {pendingInvoices.map((row) => {
                const age = daysSince(row.poDate);
                const risk = age === null ? "Unknown" : age > 60 ? "High Action" : age > 30 ? "Medium Risk" : "Low Risk";
                return (
                  <div key={row.purchaseId} className="p-3 hover:bg-slate-50 transition flex items-center justify-between gap-2">
                    <div className="space-y-0.5">
                      <p className="text-xs text-slate-800 font-semibold">{row.poNumber || `#${row.purchaseId}`}</p>
                      <p className="text-[10px] text-slate-400 font-medium">
                        {row.distributorName || "—"} · <span className="font-mono text-slate-700">{formatDate(row.poDate)}</span>
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-bold text-slate-900 font-mono">{formatINR(row.poValue)}</p>
                      <span className={`text-[8px] font-black uppercase tracking-wide px-1 rounded ${
                        risk === "Low Risk" ? "bg-slate-100 text-slate-600" :
                        risk === "Medium Risk" ? "bg-amber-50 text-amber-700" :
                        "bg-rose-100 text-rose-700 animate-pulse"
                      }`}>
                        {risk}
                      </span>
                    </div>
                  </div>
                );
              })}
              {pendingInvoices.length === 0 && !metricsLoading && (
                <p className="text-[11px] text-slate-400 text-center p-4">All distributor invoices received 🎉</p>
              )}
            </div>

            <button className="w-full text-center p-3 text-xs font-semibold border-t border-slate-100 bg-slate-50 text-slate-600 hover:bg-slate-100/80 transition flex items-center justify-center gap-0.5">
              Follow Up With Distributors <ArrowUpRight size={14} />
            </button>
          </div>

        </div>

        {/* MONTHLY PURCHASE VALUE BAR CHART */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 p-4 border-b bg-white">
            <div className="flex items-center gap-2">
              <BarChart3 size={18} className="text-blue-600" />
              <div>
                <span className="text-sm font-semibold text-slate-800 block">Monthly Purchase Value</span>
                <span className="text-[11px] text-gray-400">
                  {activeQuarterTab === "All"
                    ? `PO value raised per month · FY ${metrics?.fyLabel || ""} (Apr–Mar)`
                    : `${activeQuarterTab} PO value by month · FY ${metrics?.fyLabel || ""}`}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-600 inline-block" /> PO Value</div>
            </div>
          </div>

          <div className="p-4 sm:p-6 flex flex-col justify-between min-h-[280px]">
            <div className="relative w-full h-40 sm:h-48">
              <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                {monthlyGridlines.map((g) => (
                  <div key={g} className="w-full border-t border-slate-100 text-[9px] text-slate-400 pt-0.5">
                    {formatCompactINR(g)}
                  </div>
                ))}
                <div className="w-full border-b border-slate-200 text-[9px] text-slate-400 pb-0.5">0</div>
              </div>

              <div className="absolute inset-0 flex items-end justify-between gap-1 sm:gap-3 px-1 sm:px-2">
                {monthlyData.map((d) => (
                  <div key={d.month} className="flex-1 flex flex-col items-center justify-end h-full">
                    <div className="relative group/bar flex flex-col items-center justify-end h-full w-6 sm:w-8">
                      <div className="absolute -top-1 -translate-y-full opacity-0 group-hover/bar:opacity-100 transition-opacity duration-150 bg-slate-800 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-md shadow-lg whitespace-nowrap z-10 pointer-events-none">
                        {formatCompactINR(d.value)}
                      </div>
                      <div
                        className="w-full rounded-t-md transition-all duration-500 cursor-pointer bg-blue-600 hover:bg-blue-700"
                        style={{ height: `${barHeightPct(d.value)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-start justify-between gap-1 sm:gap-3 px-1 sm:px-2 mt-2">
              {monthlyData.map((d) => (
                <span key={d.month} className="flex-1 text-center text-[10px] sm:text-xs font-bold text-slate-600">{d.month}</span>
              ))}
            </div>

            <div className="mt-6 bg-slate-50 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
              <span>{activeQuarterTab === "All" ? "FY Purchase Value" : `${activeQuarterTab} Purchase Value`}: <strong>{formatINR(fyTotalPOValue)}</strong></span>
              <span>Average Monthly Value: <strong>{formatCompactINR(avgMonthlyValue)}</strong></span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}